/* istanbul ignore file */
import { useSecurityFormMapper } from 'framework/form';
import { FormFieldsData } from 'validation-schema-library/build/validation/types';
import { updateEmployeePermissions } from './model';
import { EMPLOYEE_TYPES_ALIAS } from './constants';

export default () => {
  const { filterForm } = useSecurityFormMapper(updateEmployeePermissions);

  const checkRequisitesInformation = (formData: FormFieldsData) => {
    if (
      formData.employeeType !== EMPLOYEE_TYPES_ALIAS.CONTRACTOR &&
      formData.extendedInformation?.requisitesInformation
    ) {
      delete formData.extendedInformation.requisitesInformation; // eslint-disable-line no-param-reassign
    }
  };

  const createMapper = (employee: any) => {
    const preparedData = { ...employee };

    checkRequisitesInformation(preparedData);

    return preparedData;
  };

  // set optional family member middleName to null if field is empty
  const normalizeFamily = (members = []) => {
    if (members != null) {
      return members.map((item: any) =>
        item.name.middleName
          ? item
          : {
              ...item,
              name: {
                ...item.name,
                middleName: null,
              },
            },
      );
    }
    return [];
  };

  const mapIdentityDocument = (document: any = {}) => {
    return {
      ...document,
      scansIds: document.scansIds ? document.scansIds.map((file: { artifactId: any }) => file.artifactId || file) : [],
    };
  };

  const mapForUpdate = (data: FormFieldsData) => {
    const { extendedInformation, mentees } = data;
    const { photosIds, identityDocuments, locationUpdateDate, other, children } = extendedInformation || {};

    const mappedData = {
      ...data,
      specializationInfo: {
        ...data.specializationInfo,
        competencyLevel:
          data?.specializationInfo?.competencyLevel === '' ? null : data?.specializationInfo?.competencyLevel,
      },
      mentees: (() => {
        if (!Array.isArray(mentees)) return [];
        if (mentees.length > 0 && typeof mentees[0] === 'string') return mentees;
        return mentees?.map((item: any) => item.alias || item.value);
      })(),
      ...(extendedInformation
        ? {
            extendedInformation: {
              ...extendedInformation,
              photosIds: photosIds && photosIds.map((file: { artifactId: any }) => file.artifactId || file),
              identityDocuments: identityDocuments.map((identityDocument: {} | undefined) => {
                return mapIdentityDocument(identityDocument);
              }),
              familyMembers: [...normalizeFamily(other), ...normalizeFamily(children)],
              locationUpdateDate: locationUpdateDate === null || locationUpdateDate === '' ? ' ' : locationUpdateDate,
            },
          }
        : {}),
    };

    return filterForm(mappedData);
  };

  const updateMapper = (data: FormFieldsData) => {
    checkRequisitesInformation(data);
    return mapForUpdate(data);
  };

  const personalInfoMapper = (data: FormFieldsData) => {
    const { extendedInformation, employeeType } = data;
    const { photosIds, requisitesInformation } = extendedInformation || {};

    return {
      nameLocal: data.nameLocal,
      nameEn: data.nameEn,
      workingEmail: data.workingEmail,
      gender: data.gender,
      employeeType: employeeType,
      positionId: data.positionId,
      extendedInformation: {
        photosIds: photosIds && photosIds.map((file: { artifactId: any }) => file.artifactId || file),
        dateOfBirth: data.extendedInformation?.dateOfBirth,
        hrInformation: {
          workplaceLocation: data.extendedInformation?.hrInformation?.workplaceLocation,
          roomNumber: data.extendedInformation?.hrInformation?.roomNumber,
          deskLocation: data.extendedInformation?.hrInformation?.deskLocation,
        },
        birthdayCongratsConsent: data.extendedInformation?.birthdayCongratsConsent,
        ...(requisitesInformation && employeeType === EMPLOYEE_TYPES_ALIAS.CONTRACTOR ? { requisitesInformation } : []),
      },
    };
  };

  const specializationInfoMapper = (data: FormFieldsData) => {
    const { extendedInformation } = data;
    const { photosIds, identityDocuments, locationUpdateDate, other, children } = extendedInformation || {};

    const mappedData = {
      ...data,
      ...(data.specializationInfo && {
        specializationInfo: {
          ...data.specializationInfo,
          competencyLevel:
            data?.specializationInfo?.competencyLevel === '' ? null : data?.specializationInfo?.competencyLevel,
        },
      }),
      ...(extendedInformation
        ? {
            extendedInformation: {
              ...extendedInformation,
              photosIds: photosIds && photosIds.map((file: { artifactId: any }) => file.artifactId || file),
              identityDocuments: identityDocuments.map((identityDocument: {} | undefined) => {
                return mapIdentityDocument(identityDocument);
              }),
              familyMembers: [...normalizeFamily(other), ...normalizeFamily(children)],
              locationUpdateDate: locationUpdateDate === null || locationUpdateDate === '' ? ' ' : locationUpdateDate,
            },
          }
        : {}),
    };

    const filteredData = filterForm(mappedData);

    delete filteredData.alias;
    delete filteredData.mentorshipInformation;

    return filteredData;
  };

  const mentorshipInfoMapper = (data: FormFieldsData) => {
    const { mentorshipInformation } = data;

    const mappedData = {
      mentorshipInformation: {
        mentorAlias: mentorshipInformation?.mentor || null,
        menteeAliases: (() => {
          if (!Array.isArray(mentorshipInformation.mentees)) return [];
          if (mentorshipInformation.mentees.length > 0 && typeof mentorshipInformation.mentees[0] === 'string')
            return mentorshipInformation.mentees;
          return mentorshipInformation.mentees?.map((item: any) => item.alias || item.value);
        })(),
      },
    };

    return filterForm(mappedData);
  };

  const identityDocumentsMapper = (data: FormFieldsData) => {
    return {
      extendedInformation: {
        identityDocuments: data.extendedInformation?.identityDocuments,
      },
    };
  };

  const contactInfoMapper = (data: FormFieldsData) => {
    return {
      extendedInformation: {
        personalPhoneNumber: data.extendedInformation?.personalPhoneNumber,
        personalEmail: data.extendedInformation?.personalEmail,
        otherPhoneNumber: data.extendedInformation?.otherPhoneNumber,
        additionalContactInfo: data.extendedInformation?.additionalContactInfo,
        registrationAddress: data.extendedInformation?.registrationAddress,
        homeAddress: data.extendedInformation?.homeAddress,
      },
    };
  };

  const accountsMapper = (data: FormFieldsData) => {
    return {
      azureId: data.azureId,
      extendedInformation: {
        financialInformation: {
          financialOneCId: data.extendedInformation?.financialInformation?.financialOneCId,
        },
      },
    };
  };

  const familyInfoMapper = (data: FormFieldsData) => {
    const otherMembers = data.extendedInformation?.other || [];
    const childrenMembers = data.extendedInformation?.children || [];

    return {
      extendedInformation: {
        familyMembers: [...otherMembers, ...childrenMembers],
        other: otherMembers,
        children: childrenMembers,
      },
    };
  };

  const peopleManagementMapper = (data: FormFieldsData) => {
    return {
      assignedPeopleManagerAliases: data.assignedPeopleManagerAliases,
      hasSpecificManagers: data.hasSpecificManagers,
      isAccessForAllPeopleManagers: data.isAccessForAllPeopleManagers,
    };
  };

  return {
    createMapper,
    updateMapper,
    personalInfoMapper,
    specializationInfoMapper,
    mentorshipInfoMapper,
    identityDocumentsMapper,
    contactInfoMapper,
    accountsMapper,
    familyInfoMapper,
    peopleManagementMapper,
    normalizeFamily,
  };
};
